import { combineValidateRules } from 'legacy/lut/helpers/combineValidateRules'
import React, { FC, useCallback, useContext, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'

import { downloadBlob } from 'clickNgo/helpers/common'
import { Button } from 'core-components/library/button'
import { Dropdown } from 'core-components/library/dropdown'
import { Table } from 'core-components/library/list'
import {
  Controller,
  IControllerResult,
  ValidateInput,
  ValidationStatus,
} from 'core-components/library/validation'
import { getTokens } from 'core-components/sitebuilder/helpers/getTokens'
import { selectAppOwnerId } from 'core-components/sitebuilder/store/selectors/selectAppOwnerId'
import { Static } from 'core-components/types'
import { Crud } from 'core/types/Crud'
import Logger from 'core/utils/Logger'
import { showError } from 'onboarding/classes/services/notification'
import { APLAMSTranslationCodes } from 'onboarding/configs/translations/aplAMSTranslationCodes'
import { LanguageContext } from 'onboarding/context/LanguageContext'
import { aplService } from 'onboarding/services/aplService'
import { REGEX_COMPANY_INVALID_CHARACTERS } from 'src/config/regexp'

import { TABLE_PAGINATION_OPTIONS } from './configs'
import { getTableColumns } from './helpers/getTableColumns'
import { useAmsDataCrud } from './hooks/useAmsDataCrud'

import styles from './PageContent.module.scss'

export const PageContent: FC = () => {
  const appOwnerId = useSelector(selectAppOwnerId)
  const { t } = useContext(LanguageContext)

  const [canSearch, setCanSearch] = useState(false)
  const [isRequiredValidation, setIsRequiredValidation] = useState(false)
  const [isExporting, setIsExporting] = useState(false)

  const [companyName, setCompanyName] = useState('')
  const [country, setCountry] = useState('')
  const [locationId, setLocationId] = useState('')

  const columns = getTableColumns(t)
  const { crud, handleSearch } = useAmsDataCrud({
    companyName,
    country,
    locationId,
  })

  const valueDependencies = useMemo(() => {
    return [country, locationId, companyName]
  }, [country, locationId, companyName])

  const countryCrud = useMemo(() => {
    return new Crud({
      fetcher: new Static([
        { id: 'us', value: 'USA' },
        { id: 'ca', value: 'Canada' },
      ]),
      keyField: 'id',
    })
  }, [])

  const handleValidationStatusChange = useCallback((result: IControllerResult) => {
    if (result.status === ValidationStatus.Valid) {
      setCanSearch(true)
    } else if (result.status === ValidationStatus.Invalid) {
      setCanSearch(false)
    }
  }, [])

  const setValidationToRequired = useCallback(() => {
    setIsRequiredValidation(true)
  }, [])

  const prepareErrorMessageFromMatched = useCallback((matched: RegExpMatchArray) => {
    if (!matched) {
      return ''
    }

    const charsInMatches = matched.reduce((previousValue: string[], group: string) => {
      return [...previousValue, ...Array.from(group)]
    }, [])

    const set = new Set(charsInMatches)
    return `Should not contains: ${Array.from(set).join(' ')}`
  }, [])

  const prepareErrorMessageFromTest = useCallback((regexp: RegExp, value: string) => {
    const invalidChar = value.split('').find((char) => !regexp.test(char))
    return `Should not contains: ${invalidChar}`
  }, [])

  const checkCompanyName = useCallback(
    async (value: string) => {
      if (!isRequiredValidation) {
        return ''
      }

      if (!locationId && country && !value) {
        return 'Required'
      }

      if (value && value.trim().length < 3) {
        return t(APLAMSTranslationCodes.search_company_name_requirements)
      }

      const matched = value.match(REGEX_COMPANY_INVALID_CHARACTERS)
      if (matched) {
        return prepareErrorMessageFromMatched(matched)
      }

      return ''
    },
    [isRequiredValidation, locationId, country, prepareErrorMessageFromMatched]
  )

  const isOneOfRequired = useCallback(async () => {
    if (!isRequiredValidation) {
      return ''
    }

    if (!companyName && !country && !locationId) {
      return 'Required Company Name and Country or Location ID'
    }

    return ''
  }, [isRequiredValidation, companyName, locationId, country])

  const checkCountry = useCallback(
    async (value: string) => {
      if (!isRequiredValidation) {
        return ''
      }

      if (!locationId && companyName && !value) {
        return 'Required'
      }

      return ''
    },
    [isRequiredValidation, locationId, companyName]
  )

  const checkLocationID = useCallback(
    async (value: string) => {
      if (!isRequiredValidation) {
        return ''
      }

      if (value && value.length < 4) {
        return t(APLAMSTranslationCodes.search_location_id_requirements)
      }

      if (!value && ((companyName && !country) || (!companyName && country))) {
        return 'Required'
      }

      if (value) {
        const matched = /^[0-9]+$/.test(value)
        if (!matched) {
          return prepareErrorMessageFromTest(/^[0-9]+$/, value)
        }
      }

      return ''
    },
    [prepareErrorMessageFromTest, companyName, country, isRequiredValidation]
  )

  const exportAMSEligiblePartners = async () => {
    const [accessToken] = getTokens(appOwnerId)
    if (!accessToken) {
      showError('Please, re-log in to the application')

      return
    }

    try {
      setIsExporting(true)
      const { fileName, file } = await aplService.exportAMSEligiblePartners(accessToken)
      downloadBlob(fileName, file)
    } catch (e: any) {
      Logger.error(e)
      showError(e.message)
    } finally {
      setIsExporting(false)
    }
  }

  return (
    <div className={styles.content}>
      <div>{t(APLAMSTranslationCodes.search_description)}</div>
      <div className={styles.searchArea}>
        <Controller onValidationStatusChange={handleValidationStatusChange}>
          <ValidateInput
            onKeyDown={setValidationToRequired}
            valueDependencies={valueDependencies}
            classes={{ validatorClasses: { root: styles.searchArea__input } }}
            value={companyName}
            onChange={setCompanyName}
            label={t(APLAMSTranslationCodes.company_name_filter)}
            checkFn={combineValidateRules([checkCompanyName, isOneOfRequired])}
          />
          <Dropdown
            valueDependencies={valueDependencies}
            checkFn={combineValidateRules([checkCountry, isOneOfRequired])}
            onBlur={() => {
              setIsRequiredValidation(true)
            }}
            classes={{ controlClasses: { root: styles.searchArea__input } }}
            source={countryCrud}
            value={country}
            caption="value"
            onChange={setCountry}
            label={t(APLAMSTranslationCodes.country_filter)}
          />
          <ValidateInput
            valueDependencies={valueDependencies}
            onKeyDown={() => {
              setIsRequiredValidation(true)
            }}
            classes={{ validatorClasses: { root: styles.searchArea__input } }}
            value={locationId}
            onChange={setLocationId}
            label={'Location ID'}
            checkFn={combineValidateRules([checkLocationID, isOneOfRequired])}
          />
          <Button
            className={styles.searchArea__button}
            onClick={handleSearch}
            disabled={!canSearch || !isRequiredValidation}
          >
            {t(APLAMSTranslationCodes.search_button)}
          </Button>
        </Controller>
      </div>

      <Table
        header
        isHeaderSticky
        hasExport={false}
        pagination
        rowsPerPageOptions={TABLE_PAGINATION_OPTIONS}
        source={crud}
        columns={columns}
      />
      <div className={styles.export}>
        <Button onClick={exportAMSEligiblePartners} loading={isExporting}>
          Export
        </Button>
      </div>
    </div>
  )
}
